import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

type PushStatus = "default" | "granted" | "denied" | "unsupported" | "loading";

interface PushState {
  status: PushStatus;
  subscribed: boolean;
  error: string | null;
}

const STORAGE_KEY = "push-subscription-endpoint";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export function usePushNotifications() {
  const [state, setState] = useState<PushState>({
    status: "loading",
    subscribed: false,
    error: null,
  });

  const checkSubscription = useCallback(async () => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setState({ status: "unsupported", subscribed: false, error: null });
      return;
    }
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setState({
        status: Notification.permission as PushStatus,
        subscribed: !!subscription,
        error: null,
      });
    } catch {
      setState((s) => ({ ...s, status: Notification.permission as PushStatus }));
    }
  }, []);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const urlB64ToUint8Array = useCallback(urlBase64ToUint8Array, []);

  const subscribe = useCallback(async () => {
    try {
      setState((s) => ({ ...s, error: null }));

      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setState((s) => ({ ...s, status: "unsupported", error: "Push not supported" }));
        return;
      }

      let permission = Notification.permission;
      if (permission === "default") {
        permission = await Notification.requestPermission();
      }
      if (permission === "denied") {
        setState({ status: "denied", subscribed: false, error: null });
        return;
      }

      const vapidRes = await api.get<{ publicKey: string }>("/notifications/vapid-key");
      if (vapidRes.error || !vapidRes.data?.publicKey) {
        setState((s) => ({ ...s, error: "Failed to get VAPID key" }));
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      let subscription = await registration.pushManager.getSubscription();

      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(vapidRes.data.publicKey) as BufferSource,
        });
      }

      const subData = subscription.toJSON();
      const res = await api.post("/notifications/subscribe", {
        endpoint: subData.endpoint,
        keys: subData.keys,
      });

      if (res.error) {
        setState((s) => ({ ...s, error: "Failed to save subscription" }));
        return;
      }

      try {
        localStorage.setItem(STORAGE_KEY, subscription.endpoint);
      } catch {
        /* noop */
      }
      setState({ status: "granted", subscribed: true, error: null });
    } catch (err) {
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : "Subscription failed",
      }));
    }
  }, [urlB64ToUint8Array]);

  const unsubscribe = useCallback(async () => {
    try {
      setState((s) => ({ ...s, error: null }));
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        const subData = subscription.toJSON();
        await api.post("/notifications/unsubscribe", { endpoint: subData.endpoint });
        await subscription.unsubscribe();
      }

      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* noop */
      }
      setState((s) => ({ ...s, subscribed: false }));
    } catch (err) {
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : "Unsubscription failed",
      }));
    }
  }, []);

  return {
    ...state,
    subscribe,
    unsubscribe,
    checkSubscription,
  };
}
